/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드 트리 클래스를 정의한다.
 *
 * @file /src/components/grid/Tree.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 13.
 */
import * as parent from '@components/grid/Grid';
import { TreeStores } from '@models/store/Store';
import './Tree.css';

export interface Listeners<T extends Tree> extends parent.Listeners<T> {}

export interface Properties<T extends Tree> extends parent.Properties<TreeStores, T> {
    /**
     * 데이터스토어 로드시 자식레코드가 포함된 경우 자동으로 확장할지 여부
     */
    autoExpand?: boolean;

    /**
     * 확장할 트리뎁스
     */
    expandedDepth?: number;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Tree extends parent.Grid<TreeStores, Tree> {
    /**
     * 모드
     */
    mode = 'tree';

    /**
     * 자식 레코드가 존재할 경우 자동으로 확장할지 여부
     */
    autoExpand: boolean = false;

    /**
     * 확장할 트리뎁스 (true 인 경우 모든 행을 확장한다.)
     */
    expandedDepth: boolean | number | null = null;

    /**
     * 그리드 트리 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Tree> = null) {
        super(properties);

        this.autoExpand = this.properties.autoExpand === true;
        this.expandedDepth = this.properties.expandedDepth ?? null;
    }

    /**
     * 전체트리를 확장한다.
     *
     * @param expandedDepth - 확장을 제한할 최대 깊이 (true 인 경우 모든 행을 확장한다.)
     */
    async expandAll(expandedDepth: boolean | number = true): Promise<void> {
        await this.body.expandAll(expandedDepth);
    }

    /**
     * 데이터스토어가 로딩되었을 때
     */
    async onLoad(): Promise<void> {
        await super.onLoad(false);

        if ((await this.body.restoreExpand()) == false) {
            if (this.autoExpand == true) {
                await this.body.autoExpand();
            }

            if (this.expandedDepth !== null) {
                await this.expandAll(this.expandedDepth);
            }
        }

        await this.fireEvent('load', [this]);
        await this.fireEvent('update', [this]);
    }
}
